<template>
  <b-modal
    :active="true"
    :can-cancel="['escape', 'x', 'outside']"
    @close="onClose"
  >
    <div class="wi-modal-shell">
      <div class="form-container">
        <div v-if="isInitializing" class="wi-loading-state">
          <b-loading :active="true" :is-full-page="false" :can-cancel="false" />
          <p>{{ $t('work_instructions.loading') }}</p>
        </div>

        <template v-else>
          <h4>
            {{ initialData ? resolvedEditTitle : resolvedCreateTitle }}
          </h4>

          <md-field>
            <label>{{ $t('work_instructions.fields.title') }}</label>
            <md-input v-model="title" required></md-input>
          </md-field>

          <md-field>
            <label>Description</label>
            <md-input v-model="description"></md-input>
          </md-field>

          <md-field>
            <v-select
              :placeholder="$t('work_instructions.placeholders.product_line')"
              v-model="productLineSelected"
              class="selectProdLine"
              :options="productsNames"
            ></v-select>
          </md-field>

          <md-field>
            <v-select
              :placeholder="$t('work_instructions.placeholders.original_oem')"
              :disabled="!hasProduct"
              v-model="originalOEMBrandSelected"
              class="selectProdLine"
              :options="originalOEMBrandNames"
            ></v-select>
          </md-field>

          <md-field>
            <v-select
              :placeholder="$t('work_instructions.placeholders.frame_type')"
              :disabled="!hasOEM"
              v-model="frameTypeSelected"
              class="selectProdLine"
              :options="frameTypeNames"
            ></v-select>
          </md-field>

          <md-field>
            <v-select
              :placeholder="$t('work_instructions.placeholders.model')"
              :disabled="!hasFrameType"
              v-model="modelSelected"
              class="selectProdLine"
              :options="modelListName"
            ></v-select>
          </md-field>

          <md-field>
            <label>{{ $t('work_instructions.fields.part_number') }}</label>
            <md-input v-model="partNumber"></md-input>
          </md-field>

          <md-field>
            <label>{{ $t('work_instructions.fields.document_number') }}</label>
            <md-input v-model="documentNumber"></md-input>
          </md-field>

          <md-field>
            <label>{{ $t('work_instructions.fields.revision') }}</label>
            <md-input v-model="revision" required></md-input>
          </md-field>

          <md-field>
            <label>{{ $t('work_instructions.fields.sap_reference') }}</label>
            <md-input v-model="sapReference"></md-input>
          </md-field>

          <div class="buttons-container">
            <button class="cancel-button" @click="onClose">
              {{ $t('cancel') }}
            </button>
            <button
              class="submit-button"
              @click="onSave"
              :disabled="
                title === '' ||
                revision === '' ||
                !hasProduct ||
                !hasOEM ||
                !hasFrameType ||
                !hasModel
              "
            >
              {{ $t('done') }}
            </button>
          </div>
        </template>
      </div>
    </div>
  </b-modal>
</template>

<script>
const dropdownCache = {
  productsNames: [],
  productByName: {},
  oemByProductId: {},
  frameByOemId: {},
  modelByFrameId: {},
};

export default {
  name: 'CreateStandardWorkInstructionModal',
  props: {
    initialData: {
      type: Object,
      default: null,
    },
    createTitle: {
      type: String,
      default: '',
    },
    editTitle: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      title: this.initialData?.title || '',
      description: this.initialData?.description || '',
      partNumber: this.initialData?.partNumber || '',
      documentNumber: this.initialData?.documentNumber || '',
      revision: this.initialData?.revision || '',
      sapReference: this.initialData?.sapReference || '',
      hasProduct: false,
      hasOEM: false,
      hasFrameType: false,
      hasModel: false,
      productsNames: [...dropdownCache.productsNames],
      productLineSelected: null,
      originalOEMBrandNames: [],
      originalOEMBrandSelected: null,
      frameTypeNames: [],
      frameTypeSelected: null,
      modelListName: [],
      modelSelected: null,
      productByName: { ...dropdownCache.productByName },
      oemCacheByProductId: { ...dropdownCache.oemByProductId },
      frameCacheByOemId: { ...dropdownCache.frameByOemId },
      modelCacheByFrameId: { ...dropdownCache.modelByFrameId },
      isHydratingSelection: false,
      suspendDependentWatchers: true,
      isInitializing: true,
    };
  },
  computed: {
    resolvedCreateTitle() {
      return this.createTitle || this.$t('work_instructions.create_title');
    },
    resolvedEditTitle() {
      return this.editTitle || this.$t('work_instructions.edit_title');
    },
    productListName() {
      return this.$store.state.productListName;
    },
    originalOEMBrandList() {
      return this.$store.state.originalOEMBrandList;
    },
    frameTypeList() {
      return this.$store.state.frameTypeList;
    },
    modelList() {
      return this.$store.state.modelList;
    },
  },
  watch: {
    async productLineSelected(newValue, oldValue) {
      if (this.isHydratingSelection || this.suspendDependentWatchers) {
        return;
      }

      await this.onProductChanged(newValue, newValue !== oldValue);
    },

    async originalOEMBrandSelected(newValue, oldValue) {
      if (this.isHydratingSelection || this.suspendDependentWatchers) {
        return;
      }

      await this.onOemChanged(newValue, newValue !== oldValue);
    },

    async frameTypeSelected(newValue, oldValue) {
      if (this.isHydratingSelection || this.suspendDependentWatchers) {
        return;
      }

      await this.onFrameChanged(newValue, newValue !== oldValue);
    },

    modelSelected(selectedName) {
      this.hasModel = Boolean(selectedName);
    },
  },
  async mounted() {
    try {
      this.suspendDependentWatchers = true;
      this.isInitializing = true;
      await this.ensureProductOptions();
      if (this.initialData) {
        await this.prefillForEdit();
      }
      await this.$nextTick();
    } finally {
      this.suspendDependentWatchers = false;
      this.isInitializing = false;
    }
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    coerceDisplayValue(value) {
      if (Array.isArray(value)) {
        return this.coerceDisplayValue(value[0]);
      }
      if (value && typeof value === 'object') {
        return (
          value.name ||
          value.text ||
          value.value ||
          value.label ||
          value.Model ||
          value.OriginalOEM ||
          ''
        );
      }
      return value == null ? '' : String(value);
    },

    extractOptionName(entry) {
      return this.coerceDisplayValue(entry).trim();
    },

    normalizeText(value) {
      return this.coerceDisplayValue(value)
        .trim()
        .replace(/\s+/g, ' ')
        .toLowerCase();
    },

    getFirstAvailableValue(source, keys) {
      return (
        keys
          .map((key) => this.coerceDisplayValue(source?.[key]))
          .find((normalizedValue) => normalizedValue !== '') || ''
      );
    },

    findByName(entries, name) {
      const normalizedName = this.normalizeText(name);
      if (!normalizedName) {
        return null;
      }
      return (
        (entries || []).find(
          (entry) =>
            this.normalizeText(this.extractOptionName(entry)) === normalizedName
        ) || null
      );
    },

    getOptionName(entries, value) {
      const matchedEntry = this.findByName(entries, value);
      return matchedEntry ? this.extractOptionName(matchedEntry) : null;
    },

    getUniqueSortedNames(entries) {
      return [
        ...new Set(
          (entries || []).map((entry) => this.extractOptionName(entry))
        ),
      ]
        .filter((name) => Boolean(name))
        .sort();
    },

    indexByName(entries) {
      return (entries || []).reduce((map, entry) => {
        const entryName = this.extractOptionName(entry);
        if (entryName) {
          map[entryName] = entry;
        }
        return map;
      }, {});
    },

    clearOemAndBelow() {
      this.originalOEMBrandNames = [];
      this.originalOEMBrandSelected = null;
      this.hasOEM = false;
      this.clearFrameAndBelow();
    },

    clearFrameAndBelow() {
      this.frameTypeNames = [];
      this.frameTypeSelected = null;
      this.hasFrameType = false;
      this.clearModelOnly();
    },

    clearModelOnly() {
      this.modelListName = [];
      this.modelSelected = null;
      this.hasModel = false;
    },

    async ensureProductOptions() {
      if (
        !this.productsNames.length ||
        !this.productByName ||
        !Object.keys(this.productByName).length
      ) {
        await this.$store.dispatch('getProductNameList');
        this.productsNames = this.getUniqueSortedNames(this.productListName);
        this.productByName = this.indexByName(this.productListName);
        dropdownCache.productsNames = [...this.productsNames];
        dropdownCache.productByName = { ...this.productByName };
      }
      return this.productByName;
    },

    async ensureOemOptionsByProductId(productId) {
      if (!productId) {
        return [];
      }
      if (!this.oemCacheByProductId[productId]) {
        await this.$store.dispatch('getOriginalOEMBrandList', [productId]);
        this.oemCacheByProductId = {
          ...this.oemCacheByProductId,
          [productId]: [...this.originalOEMBrandList],
        };
        dropdownCache.oemByProductId = { ...this.oemCacheByProductId };
      }
      return this.oemCacheByProductId[productId];
    },

    async ensureFrameOptionsByOemId(oemId) {
      if (!oemId) {
        return [];
      }
      if (!this.frameCacheByOemId[oemId]) {
        await this.$store.dispatch('getFrameTypeList', [oemId]);
        this.frameCacheByOemId = {
          ...this.frameCacheByOemId,
          [oemId]: [...this.frameTypeList],
        };
        dropdownCache.frameByOemId = { ...this.frameCacheByOemId };
      }
      return this.frameCacheByOemId[oemId];
    },

    async ensureModelOptionsByFrameId(frameId) {
      if (!frameId) {
        return [];
      }
      if (!this.modelCacheByFrameId[frameId]) {
        await this.$store.dispatch('getModelsList', [frameId]);
        this.modelCacheByFrameId = {
          ...this.modelCacheByFrameId,
          [frameId]: [...this.modelList],
        };
        dropdownCache.modelByFrameId = { ...this.modelCacheByFrameId };
      }
      return this.modelCacheByFrameId[frameId];
    },

    async onProductChanged(selectedProductName, shouldResetChildren = true) {
      if (shouldResetChildren) {
        this.clearOemAndBelow();
      }

      this.hasProduct = Boolean(selectedProductName);
      if (!selectedProductName) {
        return;
      }

      await this.ensureProductOptions();
      const product = this.findByName(
        this.productListName,
        selectedProductName
      );
      if (!product?._id) {
        return;
      }

      const oemOptions = await this.ensureOemOptionsByProductId(product._id);
      this.originalOEMBrandNames = this.getUniqueSortedNames(oemOptions);
    },

    async onOemChanged(selectedOemName, shouldResetChildren = true) {
      if (shouldResetChildren) {
        this.clearFrameAndBelow();
      }

      this.hasOEM = Boolean(selectedOemName);
      if (!selectedOemName || !this.productLineSelected) {
        return;
      }

      await this.ensureProductOptions();
      const product = this.findByName(
        this.productListName,
        this.productLineSelected
      );
      if (!product?._id) {
        return;
      }

      const oemOptions = await this.ensureOemOptionsByProductId(product._id);
      const selectedOem = this.findByName(oemOptions, selectedOemName);
      if (!selectedOem?._id) {
        return;
      }

      const frameOptions = await this.ensureFrameOptionsByOemId(
        selectedOem._id
      );
      this.frameTypeNames = this.getUniqueSortedNames(frameOptions);
    },

    async onFrameChanged(selectedFrameTypeName, shouldResetChildren = true) {
      if (shouldResetChildren) {
        this.clearModelOnly();
      }

      this.hasFrameType = Boolean(selectedFrameTypeName);
      if (
        !selectedFrameTypeName ||
        !this.productLineSelected ||
        !this.originalOEMBrandSelected
      ) {
        return;
      }

      await this.ensureProductOptions();
      const product = this.findByName(
        this.productListName,
        this.productLineSelected
      );
      if (!product?._id) {
        return;
      }

      const oemOptions = await this.ensureOemOptionsByProductId(product._id);
      const selectedOem = this.findByName(
        oemOptions,
        this.originalOEMBrandSelected
      );
      if (!selectedOem?._id) {
        return;
      }

      const frameOptions = await this.ensureFrameOptionsByOemId(
        selectedOem._id
      );
      const selectedFrame = this.findByName(
        frameOptions,
        selectedFrameTypeName
      );
      if (!selectedFrame?._id) {
        return;
      }

      const modelOptions = await this.ensureModelOptionsByFrameId(
        selectedFrame._id
      );
      this.modelListName = this.getUniqueSortedNames(modelOptions);
    },

    async prefillForEdit() {
      const source = this.initialData || {};
      const sourceTitle = this.getFirstAvailableValue(source, [
        'title',
        'name',
      ]);
      const sourceProductLine = this.getFirstAvailableValue(source, [
        'productLine',
        'ProductLine',
      ]);
      const sourceOriginalOEM = this.getFirstAvailableValue(source, [
        'originalOEM',
        'OriginalOEM',
        'originalOem',
        'originalOEMBrand',
        'originalOemBrand',
        'oem',
      ]);
      const sourceFrameType = this.getFirstAvailableValue(source, [
        'frameType',
        'FrameType',
        'frame',
      ]);
      const sourceModel = this.getFirstAvailableValue(source, [
        'model',
        'Model',
        'modelName',
      ]);

      this.title = sourceTitle || '';
      this.description = this.getFirstAvailableValue(source, [
        'description',
        'category',
      ]);
      this.partNumber = this.getFirstAvailableValue(source, [
        'partNumber',
        'PartNumber',
      ]);
      this.documentNumber = this.getFirstAvailableValue(source, [
        'documentNumber',
        'DocumentNumber',
      ]);
      this.revision = this.getFirstAvailableValue(source, [
        'revision',
        'Revision',
      ]);
      this.sapReference = this.getFirstAvailableValue(source, [
        'sapReference',
        'SAPReference',
      ]);

      if (!sourceProductLine) {
        return;
      }

      await this.ensureProductOptions();
      const product = this.findByName(this.productListName, sourceProductLine);
      if (!product?._id) {
        return;
      }

      const oemOptions = await this.ensureOemOptionsByProductId(product._id);
      const selectedOem = this.findByName(oemOptions, sourceOriginalOEM);

      let frameOptions = [];
      let selectedFrame = null;
      let modelOptions = [];

      if (selectedOem?._id) {
        frameOptions = await this.ensureFrameOptionsByOemId(selectedOem._id);
        selectedFrame = this.findByName(frameOptions, sourceFrameType);
      }

      if (selectedFrame?._id) {
        modelOptions = await this.ensureModelOptionsByFrameId(
          selectedFrame._id
        );
      }

      this.isHydratingSelection = true;
      this.originalOEMBrandNames = this.getUniqueSortedNames(oemOptions);
      this.frameTypeNames = this.getUniqueSortedNames(frameOptions);
      this.modelListName = this.getUniqueSortedNames(modelOptions);

      this.productLineSelected =
        this.getOptionName(this.productListName, sourceProductLine) || null;
      this.originalOEMBrandSelected = selectedOem
        ? this.extractOptionName(selectedOem)
        : null;
      this.frameTypeSelected = selectedFrame
        ? this.extractOptionName(selectedFrame)
        : null;
      this.modelSelected =
        this.getOptionName(modelOptions, sourceModel) || null;

      this.hasProduct = Boolean(this.productLineSelected);
      this.hasOEM = Boolean(this.originalOEMBrandSelected);
      this.hasFrameType = Boolean(this.frameTypeSelected);
      this.hasModel = Boolean(this.modelSelected);
      await this.$nextTick();
      this.isHydratingSelection = false;
    },

    onSave() {
      const payload = {
        title: this.title,
        description: this.description,
        productLine: this.productLineSelected,
        originalOEM: this.originalOEMBrandSelected,
        frameType: this.frameTypeSelected,
        model: this.modelSelected,
        partNumber: this.partNumber,
        documentNumber: this.documentNumber,
        revision: this.revision,
        sapReference: this.sapReference,
      };
      this.$emit('save', payload);
    },
  },
};
</script>

<style lang="scss" scoped>
.wi-modal-shell {
  background: #fff;
  width: 760px;
  max-width: calc(100vw - 64px);
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  margin: 0 auto;
  padding: 16px 24px 18px;
  border-radius: 2px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
}

.form-container {
  margin: 0 auto;
  width: 100%;
  overflow: visible;
  padding: 0;

  .md-field {
    font-family: 'Roboto', 'Noto', sans-serif !important;
    -webkit-font-smoothing: antialiased;
    font-size: 20px !important;
    font-weight: 400;
    line-height: 24px;
  }

  h4 {
    font-family: 'Siemens Sans';
    color: #777;
    font-size: 24px;
    margin: 4px 0 18px;
  }

  [placeholder] {
    color: #777;
    font-size: 16px;
    font-family: 'Siemens Sans';
  }
}

.buttons-container {
  margin-top: 20px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  flex-wrap: wrap;
}

.wi-loading-state {
  position: relative;
  min-height: 380px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-direction: column;
  gap: 20px;
  color: #666;
  font-family: 'Siemens Sans';
  font-size: 18px;
  padding-bottom: 120px;
}

.cancel-button {
  float: left;
  margin-left: 20px;
  margin: 8px;
  width: 20px;
  height: 40px;
  border-radius: 1px;
  min-width: 120px;
  text-transform: capitalize;
  background-color: #f5f5f5;
  color: #4a86e8;
  border: 1px solid #4a86e8;
  font-family: 'Siemens Sans';
  font-size: 16px;
}

.submit-button {
  float: left;
  margin: 8px;
  width: 20px;
  height: 40px;
  border-radius: 1px;
  min-width: 120px;
  text-transform: capitalize;
  background-color: #4a86e8;
  color: white;
  font-family: 'Siemens Sans';
  font-size: 16px;
  border: solid 1px rgba(255, 255, 255, 0.2);
}

button:disabled,
button[disabled] {
  border: 1px solid #999999;
  background-color: #cccccc;
  color: #666666;
}

@media (max-width: 840px) {
  .wi-modal-shell {
    max-width: calc(100vw - 24px);
    padding: 14px 16px 16px;
  }

  .form-container {
    .md-field {
      font-size: 18px !important;
    }
  }
}
</style>
